import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { DollarSign, CloudRain, Sun, Share2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PriceWeatherBar = () => {
  const [price, setPrice] = useState(null);
  const [trend, setTrend] = useState(null);
  const [weather, setWeather] = useState(null);
  const [cityInput, setCityInput] = useState("");
  const [savingCity, setSavingCity] = useState(false);

  const fetchAll = async () => {
    try {
      const [p, t, w] = await Promise.all([
        axios.get(`${API}/radar/price-today`).catch(() => ({ data: {} })),
        axios.get(`${API}/market/trend`).catch(() => ({ data: null })),
        axios.get(`${API}/weather`).catch(() => ({ data: null })),
      ]);
      setPrice(p.data);
      setTrend(t.data);
      setWeather(w.data);
    } catch {
      /* silencioso */
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleSaveCity = async (e) => {
    e.preventDefault();
    if (!cityInput.trim()) return;
    setSavingCity(true);
    try {
      await axios.put(`${API}/me/city`, { city: cityInput.trim() });
      toast.success("Cidade salva!");
      fetchAll();
    } catch {
      toast.error("Erro ao salvar cidade");
    } finally {
      setSavingCity(false);
    }
  };

  const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  const fmtMes = (refDate) => {
    if (!refDate) return "";
    const [y, m] = refDate.split("-");
    return `${MESES[parseInt(m, 10) - 1]}/${y.slice(2)}`;
  };
  const fmt = (v) => (v != null ? v.toFixed(2).replace(".", ",") : "–");
  const direction = trend?.trend?.direction;
  const dirLabel = direction === "alta" ? "Em alta 📈" : direction === "queda" ? "Em queda 📉" : direction ? "Estável ➡️" : null;
  const faixa = trend?.faixa;

  const sharePrice = () => {
    const dirTxt = direction === "alta" ? "EM ALTA 📈" : direction === "queda" ? "EM QUEDA 📉" : "ESTÁVEL ➡️";
    const parts = [`🌳 Borracha: referência ${dirTxt}`];
    if (faixa?.justo) parts.push(`Referência CNA/IEA (${faixa.justo_pct}–${Math.floor(faixa.teto_pct)}%): R$ ${fmt(faixa.justo)} a ${fmt(faixa.teto)}/kg`);
    if (faixa?.market?.value) parts.push(`Último pago (IEA ${fmtMes(faixa.market.reference_date)}): R$ ${fmt(faixa.market.value)}`);
    const text = `${parts.join(" · ")} — acompanhe a tendência na HeveaTalent 👉 https://heveatalent.com.br`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
  };

  const dolar = price?.dolar;

  return (
    <div className="mb-4 grid gap-2 sm:grid-cols-2" data-testid="price-weather-bar">
      {/* Tendência + Faixa Justa */}
      <div className="bg-green-800 text-white rounded-xl p-3 flex items-center justify-between gap-2 overflow-hidden">
        <div className="flex items-center gap-3 min-w-0 flex-1 justify-center sm:justify-start text-center sm:text-left">
          <div className="w-10 h-10 bg-green-700 rounded-lg hidden sm:flex items-center justify-center shrink-0">
            <DollarSign className="w-5 h-5 text-green-200" />
          </div>
          <div className="min-w-0">
            <p className="text-[11px] text-green-200 leading-tight">Tendência do coágulo</p>
            <p className="text-base sm:text-lg font-bold leading-tight text-white" data-testid="price-today-value">
              {dirLabel && faixa?.justo
                ? `${dirLabel} · CNA/IEA ${faixa.justo_pct}–${Math.floor(faixa.teto_pct)}%: R$ ${fmt(faixa.justo)}–${fmt(faixa.teto)}`
                : dirLabel || "Aguardando tendência"}
            </p>
            <p className="text-[11px] text-green-200 leading-tight">
              {faixa?.market?.value ? `Último pago (IEA ${fmtMes(faixa.market.reference_date)}): R$ ${fmt(faixa.market.value)}` : ""}
              {dolar?.value ? `${faixa?.market?.value ? " · " : ""}Dólar R$ ${fmt(dolar.value)}` : ""}
            </p>
          </div>
        </div>
        <Button size="sm" variant="ghost" className="text-green-100 hover:text-white hover:bg-green-700 shrink-0" data-testid="share-price-btn" onClick={sharePrice}>
          <Share2 className="w-4 h-4" />
        </Button>
      </div>

      {/* Clima */}
      <div className="bg-sky-50 border border-sky-200 rounded-xl p-3" data-testid="weather-card">
        {!weather ? (
          <p className="text-xs text-gray-500">Carregando clima...</p>
        ) : weather.needs_location ? (
          <form onSubmit={handleSaveCity} className="flex items-center gap-2">
            <CloudRain className="w-5 h-5 text-sky-500 shrink-0" />
            <Input
              data-testid="weather-city-input"
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              placeholder="Sua cidade (para ver a chuva)"
              className="h-8 text-sm"
            />
            <Button type="submit" size="sm" disabled={savingCity} data-testid="weather-city-save">
              OK
            </Button>
          </form>
        ) : weather.error || (weather.hoje?.temp_max == null && weather.hoje?.chuva_prob == null) ? (
          <div className="flex items-center gap-2">
            <CloudRain className="w-5 h-5 text-sky-400 shrink-0" />
            <p className="text-xs text-gray-500" data-testid="weather-unavailable">
              Clima indisponível no momento — tentamos de novo em instantes.
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-3 justify-center sm:justify-start">
            <div className="w-10 h-10 bg-sky-100 rounded-lg flex items-center justify-center shrink-0">
              {weather.alerta_sangria ? <CloudRain className="w-5 h-5 text-sky-600" /> : <Sun className="w-5 h-5 text-amber-500" />}
            </div>
            <div className="min-w-0 text-center sm:text-left">
              <p className="text-[11px] text-gray-500 leading-tight truncate">{weather.city}</p>
              <p className="text-sm font-semibold text-gray-800 leading-tight">
                Hoje: {weather.hoje?.chuva_prob ?? "–"}% chuva · {weather.hoje?.temp_min ?? "–"}°/{weather.hoje?.temp_max ?? "–"}°
              </p>
              <p className={`text-[11px] leading-tight sm:truncate ${weather.alerta_sangria ? "text-red-600 font-semibold" : "text-gray-500"}`} data-testid="sangria-alert">
                {weather.alerta_sangria
                  ? `🌧️ Chuva amanhã (${weather.amanha?.chuva_prob ?? "?"}%) — sangria pode ser comprometida`
                  : `Amanhã: ${weather.amanha?.chuva_prob ?? "–"}% chuva`}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
